import React, { useState, useRef, useEffect } from "react";
import Papa from "papaparse";
import {
  Container,
  Box,
  Typography,
  Alert,
  Button,
  Input,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useTheme } from "@mui/material/styles";

import ProgressOverlay from "../../../common/components/ProgressOverlay";
import {
  uploadReminderExclusion,
  fetchReminderExclusionDays,
  generateReminderExclusion,
  fetchReminderExclusionAudit,
} from "../../../services/reminderModule/reminderExclusionService";

// Only the identity columns are required in the exclusion CSV.
const requiredFields = ["customerid", "customeruniqueid"];

// Exclusion list type. Option 2 is defined but disabled for now — the table
// already carries the ExcludeAllComms flag; the senders aren't wired to honor
// it yet, so the option is shown (so operators know it's coming) but not usable.
const EXCLUSION_TYPES = [
  {
    value: "reminders",
    label: "Reminders Exclusion List — exclude these customers from reminders only.",
    disabled: false,
  },
  {
    value: "all",
    label:
      "Reminders and Messaging Exclusion List — exclude from ALL communications (opt-outs, etc.). Coming soon.",
    disabled: true,
  },
];

// Format a UTC ISO instant to local date+time, no seconds.
const fmtLocalNoSecs = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const ReminderExclusionUpload = () => {
  const [errors, setErrors] = useState([]);
  const fileInputRef = useRef(null);
  const [data, setData] = useState([]);
  const [csvFile, setCsvFile] = useState(null);
  const [exclusionType, setExclusionType] = useState("reminders"); // only "reminders" selectable
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const theme = useTheme();

  // ── Section 2: comparison-generated exclusion state ───────────────────────
  const [days, setDays] = useState(null);            // { latestDate, latest:[], prior:[] }
  const [daysLoading, setDaysLoading] = useState(false);
  const [genPreview, setGenPreview] = useState(null); // { droppedCount, latestDate, optOutsToAdd, message }
  const [genBusy, setGenBusy] = useState(false);
  const [genMsg, setGenMsg] = useState("");
  const [genErr, setGenErr] = useState("");

  // ── Section 3: audit state ────────────────────────────────────────────────
  const [audit, setAudit] = useState(null);          // { summary:[], detail:[], totals:{} }
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditErr, setAuditErr] = useState("");

  useEffect(() => {
    if (successMessage) {
      const t = setTimeout(() => setSuccessMessage(""), 4000);
      return () => clearTimeout(t);
    }
    if (errorMessage) {
      const t = setTimeout(() => setErrorMessage(""), 6000);
      return () => clearTimeout(t);
    }
  }, [successMessage, errorMessage]);

  // Load the two-day comparison table + the audit on mount.
  useEffect(() => {
    loadDays();
    loadAudit();
  }, []);

  // ── Loaders ───────────────────────────────────────────────────────────────
  const loadDays = async () => {
    setDaysLoading(true);
    try {
      const d = await fetchReminderExclusionDays();
      setDays(d);
    } catch (e) {
      // non-fatal; table just won't render
    } finally {
      setDaysLoading(false);
    }
  };

  const loadAudit = async () => {
    setAuditLoading(true);
    setAuditErr("");
    try {
      const a = await fetchReminderExclusionAudit();
      setAudit(a);
    } catch (e) {
      setAuditErr(e?.response?.data?.error || "Could not load the audit.");
    } finally {
      setAuditLoading(false);
    }
  };

  // ── Section 1: CSV upload ──────────────────────────────────────────────────
  const validateRow = (row, index) => {
    const errs = [];
    requiredFields.forEach((field) => {
      const val = row[field];
      if (val === undefined || val === null || String(val).trim() === "") {
        errs.push(`${field} is required in row ${index + 1}`);
      }
    });
    return errs;
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0] || null;
    setErrors([]);
    setData([]);
    setCsvFile(null);
    if (!file) return;

    try {
      setIsLoading(true);
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (h) => h.trim().toLowerCase(),
        complete: (results) => {
          const validationErrors = [];
          results.data.forEach((row, index) => {
            validationErrors.push(...validateRow(row, index));
          });
          setErrors(validationErrors);
          if (validationErrors.length === 0) {
            setData(results.data);
            setCsvFile(file);
          }
        },
        error: (err) => {
          console.error("PapaParse error:", err);
          setErrorMessage("Could not parse CSV file.");
        },
      });
    } catch (e) {
      console.error(e);
      setErrorMessage("Could not parse CSV file.");
    } finally {
      setIsLoading(false);
    }
  };

  const reset = () => {
    setData([]);
    setErrors([]);
    setCsvFile(null);
    if (fileInputRef.current) fileInputRef.current.value = null;
  };

  const handleSave = async () => {
    if (!csvFile) {
      setErrorMessage("Please select a CSV file first.");
      return;
    }
    if (errors.length > 0) {
      setErrorMessage("Please fix CSV validation errors before uploading.");
      return;
    }
    if (exclusionType !== "reminders") {
      setErrorMessage("That exclusion list is not available yet.");
      return;
    }
    try {
      setIsLoading(true);
      const result = await uploadReminderExclusion({ file: csvFile });
      reset();
      setSuccessMessage(result?.message || "Reminders exclusion list refreshed.");
      if (result?.rejected > 0) {
        setErrorMessage(`${result.rejected} row(s) were rejected. See console for detail.`);
        if (result?.rowErrors?.length) console.warn("Exclusion upload rejects:", result.rowErrors);
      }
      loadAudit(); // refresh counts
    } catch (e) {
      console.error(e);
      setErrorMessage(
        e?.response?.data?.error || "Failed to upload exclusion list. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  // ── Section 2: generate by comparison (preview then commit) ────────────────
  const previewGenerate = async () => {
    setGenBusy(true);
    setGenErr("");
    setGenMsg("");
    setGenPreview(null);
    try {
      const d = await generateReminderExclusion({ confirm: false });
      setGenPreview(d);
    } catch (e) {
      setGenErr(e?.response?.data?.error || "Could not compute the comparison.");
    } finally {
      setGenBusy(false);
    }
  };

  const commitGenerate = async () => {
    setGenBusy(true);
    setGenErr("");
    setGenMsg("");
    try {
      const d = await generateReminderExclusion({ confirm: true });
      setGenMsg(d.message || "Exclusion list generated.");
      setGenPreview(null);
      loadAudit(); // refresh counts
    } catch (e) {
      setGenErr(e?.response?.data?.error || "Could not generate the exclusion list.");
    } finally {
      setGenBusy(false);
    }
  };

  // ── Section 3: download audit CSV (all campaigns) ──────────────────────────
  const downloadAuditCsv = () => {
    if (!audit?.detail?.length) return;
    const cols = [
      "customerid", "customeruniqueid", "campaignid", "campaignName",
      "ExcludeReminders", "ExcludeAllComms", "Reason", "Source",
      "CreatedAt", "UpdatedAt",
    ];
    const esc = (v) => {
      if (v == null) return "";
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [
      cols.join(","),
      ...audit.detail.map((r) =>
        cols.map((c) => {
          if (c === "campaignid" && r[c] == null) return esc("(no campaign reference or inactive campaigns)");
          if (c === "campaignName" && r[c] == null) return esc("(no campaign reference or inactive campaigns)");
          return esc(r[c]);
        }).join(",")
      ),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reminder_exclusions_all_campaigns_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Container width="xl" disableGutters>
      {/* ══ Section 1: Generate by comparison ═════════════════════════════ */}
      <Typography variant="h6" sx={{ mb: 1 }}>
        Generate Reminder Exclusions — latest vs. all earlier active data
      </Typography>
      <Typography variant="caption" sx={{ display: "block", mb: 2, color: "text.secondary" }}>
        The latest active dataset is authoritative. Any customer who was active in
        earlier data (on any day before the latest) but is not in the latest active
        data is assumed resolved (e.g. paid out of band) and is written to the
        reminders exclusion list. This REPLACES the current reminders-only exclusion
        set. Customers who ever replied STOP are also added as permanent opt-outs.
        Run after all of the latest files are uploaded; re-run if you upload more.
      </Typography>

      {genMsg && <Alert severity="success" sx={{ mb: 2 }}>{genMsg}</Alert>}
      {genErr && <Alert severity="error" sx={{ mb: 2 }}>{genErr}</Alert>}

      <Button
        variant="contained"
        color="primary"
        onClick={previewGenerate}
        disabled={genBusy}
        sx={{ mb: 2 }}
      >
        {genBusy ? "Working…" : "Generate Exclusion List"}
      </Button>

      {genPreview && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <>
              <Button color="inherit" size="small" onClick={commitGenerate} disabled={genBusy}>
                Confirm
              </Button>
              <Button color="inherit" size="small" onClick={() => setGenPreview(null)} disabled={genBusy}>
                Cancel
              </Button>
            </>
          }
        >
          {genPreview.message}
        </Alert>
      )}

      {daysLoading ? (
        <Typography variant="caption">Loading datasets…</Typography>
      ) : days ? (
        <Table size="small" sx={{ "& .MuiTableCell-root": { whiteSpace: "nowrap" } }}>
          <TableHead>
            <TableRow>
              <TableCell>Dataset</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Campaign</TableCell>
              <TableCell align="right">Customers</TableCell>
              <TableCell>First upload</TableCell>
              <TableCell>Last upload</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(days.latest || []).map((r, i) => (
              <TableRow key={`l-${i}`}>
                <TableCell sx={{ fontWeight: 600 }}>Latest</TableCell>
                <TableCell>{r.uploadDay}</TableCell>
                <TableCell>{r.campaignName}</TableCell>
                <TableCell align="right">{r.customerCount}</TableCell>
                <TableCell>{fmtLocalNoSecs(r.firstUploadUtc)}</TableCell>
                <TableCell>{fmtLocalNoSecs(r.lastUploadUtc)}</TableCell>
              </TableRow>
            ))}
            {(days.prior || []).map((r, i) => (
              <TableRow key={`p-${i}`}>
                <TableCell>Previous</TableCell>
                <TableCell>{r.uploadDay}</TableCell>
                <TableCell>{r.campaignName}</TableCell>
                <TableCell align="right">{r.customerCount}</TableCell>
                <TableCell>{fmtLocalNoSecs(r.firstUploadUtc)}</TableCell>
                <TableCell>{fmtLocalNoSecs(r.lastUploadUtc)}</TableCell>
              </TableRow>
            ))}
            {(!days.latest?.length && !days.prior?.length) && (
              <TableRow>
                <TableCell colSpan={6}>No active uploads found.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      ) : null}

      {/* ══ Section 2: Audit ══════════════════════════════════════════════ */}
      <Divider sx={{ my: 3 }} />

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography variant="h6">Exclusion Audit — counts by campaign</Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={downloadAuditCsv}
          disabled={!audit?.detail?.length}
        >
          Download CSV (all campaigns)
        </Button>
      </Box>

      <Typography variant="caption" sx={{ display: "block", mb: 1, color: "text.secondary" }}>
        A customer in more than one campaign is counted under each campaign (matching
        how reminders are sent per campaign), so the per-campaign rows can sum to more
        than the distinct number of excluded customers shown in the Distinct row.
      </Typography>

      {auditErr && <Alert severity="error" sx={{ mb: 2 }}>{auditErr}</Alert>}

      {auditLoading ? (
        <Typography variant="caption">Loading audit…</Typography>
      ) : audit ? (
        <Table size="small" sx={{ "& .MuiTableCell-root": { whiteSpace: "nowrap" } }}>
          <TableHead>
            <TableRow>
              <TableCell>Campaign</TableCell>
              <TableCell align="right">Reminders-only</TableCell>
              <TableCell align="right">Opt-out (all comms)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {audit.summary.length ? (
              audit.summary.map((s, i) => (
                <TableRow key={i}>
                  <TableCell>{s.campaignid == null ? "(no campaign reference or inactive campaigns)" : (s.campaignName ?? `#${s.campaignid}`)}</TableCell>
                  <TableCell align="right">{s.remindersOnly}</TableCell>
                  <TableCell align="right">{s.optOut}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow><TableCell colSpan={3}>No exclusions.</TableCell></TableRow>
            )}
            {audit.summary.length > 0 && (
              <>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Total (sum across campaigns)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{audit.totals.remindersOnly}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{audit.totals.optOut}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Distinct customers</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{audit.totals.distinctRemindersOnly}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{audit.totals.distinctOptOut}</TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      ) : null}

      {/* ══ Section 3: Manual upload (least used — placed last) ════════════ */}
      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" sx={{ mb: 1 }}>
        Manually Upload the Exclusion List
      </Typography>
      <Typography variant="caption" sx={{ display: "block", mb: 2, color: "text.secondary" }}>
        Upload a CSV with <strong>customerid</strong> and <strong>customeruniqueid</strong>.
        Customers in the file are added to the selected exclusion list. The reminders
        refresh replaces the reminders-only exclusion set: customers previously
        reminder-excluded but absent from this file are removed. Opt-out
        (all-communications) exclusions are never affected by a reminders upload.
      </Typography>

      {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}
      {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}
      {isLoading && <ProgressOverlay />}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size="auto">
          <Input
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            inputRef={fileInputRef}
            fullWidth
            sx={{
              height: "42px",
              ".MuiInputBase-input": { height: "42px" },
              '& input[type="file"]::file-selector-button': {
                backgroundColor: theme.palette.primary.main,
                color: "#fff",
                border: "0",
                borderRadius: "4px",
                padding: "0 16px",
                height: "36px",
                marginRight: "16px",
                cursor: "pointer",
              },
            }}
          />
        </Grid>

        {data && data.length > 0 && (
          <Grid size="auto">
            <Typography variant="caption" sx={{ mt: 1 }}>
              Parsed rows: {data.length}
            </Typography>
          </Grid>
        )}

        {errors.length === 0 && csvFile && (
          <Grid size="auto">
            <Button variant="contained" color="primary" onClick={handleSave}>
              Refresh Exclusion List
            </Button>
          </Grid>
        )}

        {/* List type — option 1 active, option 2 visible but disabled */}
        <Grid size="auto" sx={{ alignSelf: "flex-start" }}>
          <FormControl component="fieldset">
            <FormLabel component="legend">Exclusion type</FormLabel>
            <RadioGroup
              value={exclusionType}
              onChange={(e) => setExclusionType(e.target.value)}
            >
              {EXCLUSION_TYPES.map((t) => (
                <FormControlLabel
                  key={t.value}
                  value={t.value}
                  disabled={t.disabled}
                  control={<Radio />}
                  label={t.label}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Grid>
      </Grid>

      {errors.length > 0 && (
        <Grid size={{ xs: 12 }}>
          <Typography variant="h6" color="warning.main" className="font-bold">
            Errors found
          </Typography>
          <Box component="ul" sx={{ px: 3 }}>
            {errors.map((error, idx) => (
              <Box component="li" key={idx} sx={{ pl: 1, pb: 0.5 }}>
                {error}
              </Box>
            ))}
          </Box>
        </Grid>
      )}
    </Container>
  );
};

export default ReminderExclusionUpload;